import React, { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { useAppSelector } from '@redux/store';
import logger from '@gral/log';
import { makeStyles } from '@mui/styles';

import Sidebar from '@components/Maps/Sidebar/Sidebar';
import Map, { IMapNeeds, TMapHandle } from '@components/Maps/Map/OlMap';
import InfoSidebar from '@components/Maps/InfoSideBar';

import { CEntity, IEntity, sortListEntities, CMapLabel, CLocation, CUnitFormatter, CElement } from '@ts/ui-models';
import useEffectAfterMount from '@utils/hooks/useEffectAfterMount';

import { updateMapElement } from '@components/Maps/utils/map.utils';
import { useMapContext } from '@pages/MapPage';
import { cloneDeep, values } from 'lodash';
import { CMapLayer } from '@models/maps/Layer';
import { CMapElement, TUpdateMap } from '@models/maps/Map';

const useStyles = makeStyles(() => {
  return {
    container: {
      height: '100%',
      width: '100%',
    },
    mainContainer: {
      height: '100%',
      display: 'flex',
      flexDirection: 'row',
      flex: 1,

      overflow: 'hidden',
    },
  };
});

export type tEntityID = 'layers' | 'items' | 'labels'; //locations
export interface IFilterState {
  //noSelected no used for now
  layers: { list: CMapLayer[]; selected: Set<string>; noSelected: Set<string> };
  locations: { list: CLocation[]; selected: Set<string>; noSelected: Set<string> };
  items: { list: CMapElement[]; selected: Set<string>; noSelected: Set<string> };
  labels: { list: CMapLabel[]; selected: Set<string>; noSelected: Set<string> };
}

export interface IInheritedMapProps {
  onClickBtnSave: () => void; //Timeline
  onFindTimelinePosition: (elementID: string[] | null) => void; //Timeline
  onRefreshMap: () => void; //Map
}
interface MapViewProps {
  mapNeeds: IMapNeeds;
  inheritedMapProps: IInheritedMapProps;
}

const log = logger(`WRAPPER-MAP`);
const WrapperMapView = forwardRef<TMapHandle | undefined, MapViewProps>((props, ref) => {
  const className = useStyles();
  const { layers, items, labels, locations } = props.mapNeeds;
  const mapContext = useMapContext();
  if (!mapContext) return;
  const { view, cycleState, mapObserver } = mapContext;

  const readingTypeList = useAppSelector(state => state.global.readingTypesList);
  const unitsList = useAppSelector(state => state.global.unitsList);
  const unitFormatter = new CUnitFormatter(unitsList, readingTypeList);

  const [filtersState, setFiltersState] = useState<IFilterState>({
    layers: { list: [], selected: new Set(), noSelected: new Set() },
    locations: { list: [], selected: new Set(), noSelected: new Set() },
    items: { list: [], selected: new Set(), noSelected: new Set() },
    labels: { list: [], selected: new Set(), noSelected: new Set() },
  });
  const saveFilterStatePrev = useRef<IFilterState>(filtersState);
  const saveLastLocationSelected = useRef<CLocation | null>(null);
  const [elementsMapSelected, setElementsMapSelected] = useState<(CMapElement | CLocation)[]>([]);
  const [elementSelected, setElementSelected] = useState<CMapElement | CLocation | null>(null);

  //#region HandleChanges
  const handleClickItem = (itemID: string | null) => {
    if (!itemID) {
      setElementsMapSelected([]);
      return;
    }
    if (items.hasOwnProperty(itemID)) {
      setElementSelected(items[itemID]);
    } else if (locations && locations.hasOwnProperty(itemID)) {
      setElementSelected(locations[itemID]);
    }
    props.inheritedMapProps.onFindTimelinePosition([itemID]);
  };

  const handleClickCluster = (itemsSelected: (CMapElement | CLocation)[]) => {
    setElementsMapSelected(itemsSelected);
    if (itemsSelected.length > 20) {
      itemsSelected = itemsSelected.slice(0, 20);
    }
    props.inheritedMapProps.onFindTimelinePosition(itemsSelected.map(item => item.getID()));
  };

  const onSearchItem = (value: CEntity<IEntity>[] | null) => {
    const listCMapElement: (CMapElement | CLocation)[] = [];
    value?.map(element => {
      if (items.hasOwnProperty(element.getID())) {
        listCMapElement.push(items[element.getID()]);
      } else if (locations && locations.hasOwnProperty(element.getID())) {
        listCMapElement.push(locations[element.getID()]);
      } else {
        log.info('Item not found, creating CMapElement', element);
        const newtMapElement = updateMapElement(new CElement(element as any), unitFormatter);
        newtMapElement.layerIDs = ['customLayer'];
        listCMapElement.push(newtMapElement);
        items[newtMapElement.getID()] = newtMapElement;
        addNewTElementMapInFilters(newtMapElement);
      }
    });
    props.inheritedMapProps.onFindTimelinePosition(
      missingIds(
        listCMapElement.map(e => e.getID()),
        elementsMapSelected.map(e => e.getID())
      )
    );
    setElementsMapSelected(listCMapElement);
    if (value) {
      if (value.length > 1) setElementSelected(null);
      toggleAllEntity('items', false);
    }
  };

  const onSearchWord = (sectionID: tEntityID, value: string) => {
    if (sectionID === 'items') {
      const listFiltred = filtersState.items.list.filter(e => e.getName().toLowerCase().includes(value.toLowerCase()));
      filtersState.items.selected.clear();
      filtersState.labels.selected.clear();
      listFiltred.forEach(v => {
        filtersState.items.selected.add(v.getID());
        v.labels.forEach(l => filtersState.labels.selected.add(l.getID()));
      });
      updateLabelsBasedOnItems();
      updateLayersBasedOnItems();
      updateSetFilters();
    }
    if (sectionID === 'labels') {
      const listFiltred = filtersState.labels.list.filter(e => e.getName().toLowerCase().includes(value.toLowerCase()));
      filtersState.items.selected.clear();
      filtersState.labels.selected.clear();
      listFiltred.forEach(v => {
        filtersState.labels.selected.add(v.getID());
        v.itemIDs.forEach(i => filtersState.items.selected.add(i));
      });
      updateLayersBasedOnItems();
      updateSetFilters();
    }
  };
  //#endregion

  //#region Functions
  const missingIds = (aIds: string[], bIds: string[]): string[] => {
    const bSet = new Set(bIds);
    return aIds.filter(id => !bSet.has(id));
  };
  const updateSetFilters = () => {
    setFiltersState({
      ...filtersState,
      layers: {
        ...filtersState.layers,
        selected: new Set(filtersState.layers.selected),
      },
      locations: {
        ...filtersState.locations,
        selected: new Set(filtersState.locations.selected),
      },
      items: {
        ...filtersState.items,
        selected: new Set(filtersState.items.selected),
      },
      labels: {
        ...filtersState.labels,
        selected: new Set(filtersState.labels.selected),
      },
    });
  };
  const addNewTElementMapInFilters = (newtMapElement: CMapElement) => {
    //Layer
    const customLayer = filtersState.layers.list.find(l => l.getID() === 'customLayer');
    customLayer?.itemIDs.push(newtMapElement.getID());
    //Labels
    //Items
    filtersState.items.list.push(newtMapElement);
    filtersState.items.selected.add(newtMapElement.getID());
    filtersState.items.noSelected.delete(newtMapElement.getID());

    updateSetFilters();
  };
  /* const getListItemsAndGeometries = (): CEntity<IEntity>[] => {
    const selectedItems = filtersState.items.list
      //.filter(item => filtersState.items.selected.has(item.getID()))
      .map(item => {
        (item as any).type = 'Item';
        return item;
      });

    const listGeometries =
      Object.values(locations)
        ?.filter(geo => filtersState.locations.selected.has(geo.getID()))
        .map(geo => {
          (geo as any).type = 'Location';
          return geo;
        }) ?? [];

    return [...listGeometries, ...selectedItems];
  }; */

  //#endregion

  //#region NewFunctions Filters layers, labels, items
  const resetFiltersState = () => {
    return setFiltersState({
      ...filtersState,
      layers: {
        list: sortListEntities(
          layers.map(l => {
            l.indeterminate = false;
            return l;
          })
        ),
        selected: new Set(layers.map(l => l.getID())),
        noSelected: new Set(),
      },
      locations: {
        list: locations ? sortListEntities(Object.values(locations)) : [],
        selected: locations ? new Set(Object.keys(locations)) : new Set(),
        noSelected: new Set(),
      },
      labels: {
        list: sortListEntities(
          Object.values(labels).map(l => {
            l.indeterminate = false;
            return l;
          })
        ),
        selected: new Set(Object.keys(labels)),
        noSelected: new Set(),
      },
      items: {
        //@ts-ignore
        list: sortListEntities(Object.values(items)),
        selected: new Set(Object.keys(items)),
        noSelected: new Set(),
      },
    });
  };
  const updateLayersBasedOnItems = () => {
    filtersState.layers.list.forEach(layer => {
      const itemsForLayer = layer.itemIDs;

      // Comprobar cuántos items del layer están seleccionados
      const selectedItemsCount = itemsForLayer.filter(itemId => filtersState.items.selected.has(itemId)).length;

      if (selectedItemsCount === itemsForLayer.length) {
        // Todos los items están seleccionados
        filtersState.layers.selected.add(layer.getID());
        filtersState.layers.noSelected.delete(layer.getID());
        layer.indeterminate = false; // No está en estado indeterminado
      } else if (selectedItemsCount === 0) {
        const geometriesForLayer = layer.geometriesIds;
        if (geometriesForLayer.length > 0 || layer.background) {
          filtersState.layers.selected.add(layer.getID());
          filtersState.layers.noSelected.delete(layer.getID());
        } else {
          // Ningún item está seleccionado
          filtersState.layers.selected.delete(layer.getID());
          filtersState.layers.noSelected.add(layer.getID());
          layer.indeterminate = false; // No está en estado indeterminado
        }
      } else {
        // Algunos items están seleccionados, estado indeterminado
        layer.indeterminate = true;
      }
    });
  };

  const updateLabelsBasedOnItems = () => {
    filtersState.labels.list.forEach(label => {
      const itemsForLabel = label.itemIDs;

      // Comprobar cuántos items de la label están seleccionados
      const selectedItemsCount = itemsForLabel.filter(itemId => filtersState.items.selected.has(itemId)).length;

      if (selectedItemsCount === itemsForLabel.length) {
        // Todos los items están seleccionados
        filtersState.labels.selected.add(label.getID());
        filtersState.labels.noSelected.delete(label.getID());
        label.indeterminate = false; // No está en estado indeterminado
      } else if (selectedItemsCount === 0) {
        // Ningún item está seleccionado
        filtersState.labels.selected.delete(label.getID());
        filtersState.labels.noSelected.add(label.getID());
        label.indeterminate = false; // No está en estado indeterminado
      } else {
        // Algunos items están seleccionados, estado indeterminado
        label.indeterminate = true;
      }
    });
  };

  const toggleAllEntity = (entityID: tEntityID, isChecked: boolean, listExceptionItemsIDs?: string[]) => {
    if (entityID === 'layers') {
      setFiltersState({
        ...filtersState,
        layers: {
          list: filtersState.layers.list.map(l => {
            l.indeterminate = false;
            return l;
          }),
          selected: isChecked ? new Set(filtersState.layers.list.map(item => item.getID())) : new Set(),
          noSelected: !isChecked ? new Set(filtersState.layers.list.map(item => item.getID())) : new Set(),
        },
        locations: {
          list: filtersState.locations.list,
          selected: isChecked ? new Set(filtersState.locations.list.map(item => item.getID())) : new Set(),
          noSelected: !isChecked ? new Set(filtersState.locations.list.map(item => item.getID())) : new Set(),
        },
        labels: {
          list: filtersState.labels.list.map(l => {
            l.indeterminate = false;
            return l;
          }),
          selected: isChecked ? new Set(filtersState.labels.list.map(item => item.getID())) : new Set(),
          noSelected: !isChecked ? new Set(filtersState.labels.list.map(item => item.getID())) : new Set(),
        },
        items: {
          list: filtersState.items.list,
          selected: isChecked ? new Set(filtersState.items.list.map(item => item.getID())) : new Set(),
          noSelected: !isChecked ? new Set(filtersState.items.list.map(item => item.getID())) : new Set(),
        },
      });
    } else if (entityID === 'labels' || entityID === 'items') {
      filtersState.items.selected = isChecked ? new Set(filtersState.items.list.map(item => item.getID())) : new Set();
      filtersState.items.noSelected = !isChecked ? new Set(filtersState.items.list.map(item => item.getID())) : new Set();

      if (listExceptionItemsIDs) {
        if (!isChecked) {
          filtersState.items.selected = new Set(listExceptionItemsIDs);
        } else {
          filtersState.items.selected.forEach(itemId => {
            if (!listExceptionItemsIDs.includes(itemId)) {
              filtersState.items.selected.delete(itemId);
            }
          });
        }
      }

      updateLabelsBasedOnItems();
      updateLayersBasedOnItems();
      updateSetFilters();
    }
  };

  const toggleLayer = (layerId: string, isChecked: boolean) => {
    const layer = filtersState.layers.list.find(l => l.getID() === layerId);
    if (!layer) return;
    layer.indeterminate = false;

    // Actualiza el estado de la layer en el set de selected layers
    if (isChecked) {
      filtersState.layers.selected.add(layerId); // Seleccionar layer
      filtersState.layers.noSelected.delete(layerId);
    } else {
      filtersState.layers.selected.delete(layerId); // Deseleccionar layer
      filtersState.layers.noSelected.add(layerId);
    }

    // Manejar los items de la layer
    layer.itemIDs
      .filter(itemId => itemId !== 'fromRuleset')
      .forEach(itemId => {
        if (isChecked) {
          // Si estamos seleccionando la layer, seleccionamos también todos sus items
          filtersState.items.selected.add(itemId);
          filtersState.items.noSelected.delete(itemId);
        } else {
          // Si estamos deseleccionando la layer, deseleccionamos sus items
          filtersState.items.selected.delete(itemId);
          filtersState.items.noSelected.add(itemId);
        }
      });
    layer.geometriesIds.forEach(geometryId => {
      if (isChecked) {
        // Si estamos seleccionando la layer, seleccionamos también todos sus geometriesId
        filtersState.locations.selected.add(geometryId);
        filtersState.locations.noSelected.delete(geometryId);
      } else {
        // Si estamos deseleccionando la layer, deseleccionamos sus geometriesId
        filtersState.locations.selected.delete(geometryId);
        filtersState.locations.noSelected.add(geometryId);
      }
    });

    // Actualizar labels asociadas a los items seleccionados/desseleccionados
    updateLabelsBasedOnItems();
    updateSetFilters();
  };

  const toggleLabel = (labelId: string, isChecked: boolean) => {
    // Encuentra la label correspondiente
    const label = filtersState.labels.list.find(l => l.id === labelId);
    if (!label) return;
    label.indeterminate = false;

    // Actualiza los items asociados a la label
    const itemsForLabel = label.itemIDs;

    // Marcar o desmarcar los items dependiendo del estado del checkbox
    itemsForLabel.forEach(itemId => {
      if (isChecked) {
        // Si estamos seleccionando la label, seleccionamos todos sus items
        filtersState.items.selected.add(itemId);
        filtersState.items.noSelected.delete(itemId);
      } else {
        // Si estamos deseleccionando la label, deseleccionamos sus items
        filtersState.items.selected.delete(itemId);
        filtersState.items.noSelected.add(itemId);
      }
    });

    // Actualizar el estado de la label en el conjunto de selected labels
    if (isChecked) {
      filtersState.labels.selected.add(labelId); // Seleccionar la label
      filtersState.labels.noSelected.delete(labelId);
    } else {
      filtersState.labels.selected.delete(labelId); // Deseleccionar la label
      filtersState.labels.noSelected.add(labelId);
    }

    // Actualizamos el estado de las demás labels afectadas
    updateLabelsBasedOnItems();

    // Actualizamos los layers asociados a los items seleccionados/deseleccionados
    updateLayersBasedOnItems();

    updateSetFilters();
  };

  const toggleItem = (itemId: string, isChecked: boolean) => {
    // Marcar o desmarcar el item según el estado del checkbox
    if (isChecked) {
      // Si estamos seleccionando el item
      filtersState.items.selected.add(itemId);
      filtersState.items.noSelected.delete(itemId);
    } else {
      // Si estamos deseleccionando el item
      filtersState.items.selected.delete(itemId);
      filtersState.items.noSelected.add(itemId);
    }

    // Actualizamos las labels asociadas al item
    const item = filtersState.items.list.find(i => i.getID() === itemId);
    if (item) {
      updateLabelsBasedOnItems(); // Actualizar el estado de las labels en base a los items seleccionados
    }

    // Actualizamos los layers asociados al item
    updateLayersBasedOnItems(); // Actualizar el estado de los layers en base a los items seleccionados

    // Asegurarse de actualizar el estado global
    updateSetFilters();
  };

  //#endregion

  const callbackObserver = useCallback(
    (tUpdateMap: TUpdateMap) => {
      const { type, updatedItem: item } = tUpdateMap;
      switch (type) {
        case 'updateReadings':
          if (elementSelected?.getID() === item.getID()) {
            log.info(`Refresh ${item.getName()} infoSideBar`);
            setElementSelected(item);
          }
          break;
        default:
          break;
      }
    },
    [elementSelected]
  );

  const useEffect01InitFiltersState = (deps: React.DependencyList) => {
    useEffect(() => {
      if (cycleState.fetchedMapNeeds) {
        log.info(5, 'Generating filtersState', props);
        resetFiltersState();
      }
    }, deps);
  };

  const useEffect02SyncLabelsElementsWithPosition = (deps: React.DependencyList) => {
    useEffectAfterMount(() => {
      //log.info('Resync items with position');
      let itemIDWithPosition = Object.values(items)
        .filter(item => item.mainFeature)
        .map(item => item.getID());

      for (const layer of layers.filter(layer => layer.itemIDs.length > 0)) {
        const listItemIDs: string[] = [];
        for (const itemID of itemIDWithPosition.filter(itemID => layer.itemIDs.includes(itemID))) {
          if (layer.itemIDs.includes(itemID)) {
            listItemIDs.push(itemID);
          }
        }
        layer.itemIDsWithPosition = listItemIDs;
      }
      for (const label of Object.values(labels)) {
        const listItemIDs: string[] = [];
        for (const itemID of itemIDWithPosition) {
          if (label.itemIDs.includes(itemID)) {
            listItemIDs.push(itemID);
          }
        }
        label.itemIDsWithPosition = listItemIDs;
      }
      setFiltersState({ ...filtersState });
    }, deps);
  };

  const useEffectChangeItemSelected = (deps: React.DependencyList) => {
    useEffectAfterMount(() => {
      log.info('ChangeFiltersState', elementsMapSelected);

      if (elementsMapSelected.length > 0) {
        elementsMapSelected.forEach(item => {
          if (!item) return;
          if (filtersState.items.noSelected.has(item.getID())) toggleItem(item.getID(), true);
        });
      }
      if (elementsMapSelected.length === 1) setElementSelected(elementsMapSelected[0]);
      if (elementsMapSelected.length === 0) {
        setElementSelected(null);
        props.inheritedMapProps.onFindTimelinePosition(null);
        toggleAllEntity('items', true);
      }
    }, deps);
  };

  const useEffectChangeLocationSelected = (deps: React.DependencyList) => {
    useEffectAfterMount(() => {
      if (elementSelected instanceof CLocation && elementSelected.itemsInsideByType) {
        log.info('Location selected', elementSelected);
        saveFilterStatePrev.current = cloneDeep(filtersState);
        saveLastLocationSelected.current = elementSelected;
        if (ref && 'current' in ref) {
          const listExceptionItemsIDs: string[] = [];
          elementSelected.itemsInsideByType.forEach(type => {
            for (const item of type.items) {
              listExceptionItemsIDs.push(item.logicalId);
            }
          });
          toggleAllEntity('items', false, listExceptionItemsIDs);
          //ref.current?.onChangeGlobalClusterVisibility(true);
        }
      }
      return () => {
        if (saveLastLocationSelected.current !== null && saveLastLocationSelected.current instanceof CLocation) {
          log.info('Location not selected');
          saveLastLocationSelected.current = null;
          setFiltersState(saveFilterStatePrev.current);
        }
      };
    }, deps);
  };

  const useEffectResyncMapObserver = (deps: React.DependencyList) => {
    useEffect(() => {
      mapObserver.subscribe(callbackObserver);

      return () => {
        mapObserver.unsubscribe(callbackObserver);
      };
    }, deps);
  };

  useEffect01InitFiltersState([cycleState.fetchedMapNeeds]);
  useEffect02SyncLabelsElementsWithPosition([cycleState.allTimelinePositionsFetched, cycleState.reSyncData]);

  useEffectChangeItemSelected([elementsMapSelected]);
  useEffectChangeLocationSelected([elementSelected]);
  useEffectResyncMapObserver([callbackObserver]);

  return (
    <div className={className.container}>
      <div className={className.mainContainer}>
        <Sidebar
          inheritedMapProps={props.inheritedMapProps}
          filtersState={filtersState}
          elementsMapSelected={elementsMapSelected}
          toggleCheckbox={{ toggleLayer, toggleLabel, toggleItem, toggleAllEntity }}
          onSearchWord={onSearchWord}
          onSearchItem={onSearchItem}
        />
        <div className="flex w-full h-full border-t border-[#808080]">
          <Map
            ref={ref}
            mapNeeds={props.mapNeeds}
            elementsMapSelected={elementsMapSelected}
            elementSelected={elementSelected}
            //loading={loading}
            filtersState={filtersState}
            onClickItem={handleClickItem}
            onClickCluster={handleClickCluster}
            //inheritedMapProps={props.inheritedMapProps}
            //featuresCluster={state.featuresCluster}
          />
          <InfoSidebar
            view={view}
            elementsMapSelected={elementsMapSelected}
            setElementsMapSelected={setElementsMapSelected}
            elementSelected={elementSelected}
            setElementSelected={setElementSelected}
          />
        </div>
      </div>
    </div>
  );
});

export default WrapperMapView;
